import { useState } from "react";
import axios from "axios";
import { ArrowDown, ArrowUp, Loader2, Plus, Sparkles, Trash2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { FIELD_TYPE_OPTIONS, CANONICAL_MAPPING_OPTIONS, emptyField, defaultFields } from "@/components/org/intake/wizard/wizardMeta";
import { btn, outlineBtn, labelClass, inputClass, card } from "@/components/org/intake/intakeMeta";

export const FieldsStep = ({ model, onSaved, onFieldsChange }) => {
  const { API } = useAuth();
  const [fields, setFields] = useState(model.fields?.length ? model.fields : []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const emit = (next) => { setFields(next); onFieldsChange?.(next); };

  const updateField = (idx, patch) => emit(fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  const removeField = (idx) => emit(fields.filter((_, i) => i !== idx));
  const addField = () => emit([...fields, emptyField()]);
  const useDefaults = () => {
    if (fields.length > 0 && !window.confirm("This replaces your current fields with the default set (Name, Email, Phone, Address & Description), fully mapped. Continue?")) return;
    emit(defaultFields());
  };
  const move = (idx, dir) => {
    const next = [...fields];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    emit(next);
  };

  const contactRequirementMet = fields.some((f) => (f.canonical_mapping === "email" || f.canonical_mapping === "phone") && f.required);

  const save = async () => {
    setError("");
    for (const f of fields) {
      if (!f.label.trim()) {
        setError("Every field needs a label");
        return;
      }
      if ((f.type === "dropdown" || f.type === "multiple_choice") && !f.options.some((o) => o.trim())) {
        setError(`"${f.label}" needs at least one option`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { fields: fields.map((f) => ({ ...f, field_id: f.field_id.startsWith("_new_") ? null : f.field_id })) };
      const { data } = await axios.patch(`${API}/org/intake/models/${model.model_id}/fields`, payload, { withCredentials: true });
      emit(data.model.fields);
      onSaved(data.model);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save fields.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={card} data-testid="wizard-step-fields">
      <p className="text-sm font-bold text-slate-950 dark:text-white">Form Fields</p>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Map a field to First Name and Last Name, and mark your Email or Phone field as <strong>Required</strong> — an optional contact field won't satisfy activation, since customers must always be reachable.</p>

      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-blue-300 bg-blue-50/60 p-3 dark:border-blue-500/30 dark:bg-blue-500/10">
        <button type="button" onClick={useDefaults} data-testid="intake-fields-use-defaults-btn" className={outlineBtn}>
          <Sparkles size={14} /> Use Default Form Fields
        </button>
        <span className="text-xs text-slate-500 dark:text-slate-400">Instantly adds Name, Email, Phone, Address &amp; Description — already mapped, ready to activate.</span>
      </div>

      <div className="mt-4 space-y-4">
        {fields.map((f, idx) => (
          <div key={f.field_id} className="rounded-xl border border-slate-200 p-4 dark:border-white/10" data-testid={`intake-field-row-${idx}`}>
            <div className="flex items-start justify-between gap-2">
              <input
                value={f.label}
                onChange={(e) => updateField(idx, { label: e.target.value })}
                placeholder="Field label"
                data-testid={`intake-field-label-${idx}`}
                className={`${inputClass} mt-0 font-semibold`}
              />
              <div className="flex shrink-0 items-center gap-1">
                <button type="button" onClick={() => move(idx, -1)} data-testid={`intake-field-up-${idx}`} className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 dark:border-white/15 dark:text-slate-400 dark:hover:bg-white/10"><ArrowUp size={13} /></button>
                <button type="button" onClick={() => move(idx, 1)} data-testid={`intake-field-down-${idx}`} className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 dark:border-white/15 dark:text-slate-400 dark:hover:bg-white/10"><ArrowDown size={13} /></button>
                <button type="button" onClick={() => removeField(idx)} data-testid={`intake-field-remove-${idx}`} className="rounded-lg border border-red-200 p-1.5 text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10"><Trash2 size={13} /></button>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Type</label>
                <select value={f.type} onChange={(e) => updateField(idx, { type: e.target.value })} data-testid={`intake-field-type-${idx}`} className={`${inputClass} bg-white dark:bg-[#0B1220]`}>
                  {FIELD_TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Maps to Customer Attribute</label>
                <select value={f.canonical_mapping} onChange={(e) => updateField(idx, { canonical_mapping: e.target.value })} data-testid={`intake-field-mapping-${idx}`} className={`${inputClass} bg-white dark:bg-[#0B1220]`}>
                  {CANONICAL_MAPPING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-3">
              <label className={labelClass}>Placeholder / Description</label>
              <input value={f.placeholder || ""} onChange={(e) => updateField(idx, { placeholder: e.target.value })} data-testid={`intake-field-placeholder-${idx}`} className={inputClass} />
            </div>

            {(f.type === "dropdown" || f.type === "multiple_choice") && (
              <div className="mt-3">
                <label className={labelClass}>Options (comma separated)</label>
                <input
                  value={f.options.join(", ")}
                  onChange={(e) => updateField(idx, { options: e.target.value.split(",").map((o) => o.trim()) })}
                  placeholder="Option A, Option B, Option C"
                  data-testid={`intake-field-options-${idx}`}
                  className={inputClass}
                />
              </div>
            )}

            <label className="mt-3 flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
              <input type="checkbox" checked={f.required} onChange={(e) => updateField(idx, { required: e.target.checked })} data-testid={`intake-field-required-${idx}`} />
              Required
            </label>
            {(f.canonical_mapping === "email" || f.canonical_mapping === "phone") && !contactRequirementMet && (
              <p className="mt-2 text-[11px] font-semibold text-amber-600 dark:text-amber-400" data-testid={`intake-field-contact-warning-${idx}`}>
                This field must be Required (or map another contact field as Required) before you can activate this model.
              </p>
            )}
          </div>
        ))}

        <button type="button" onClick={addField} data-testid="intake-field-add-btn" className={outlineBtn}>
          <Plus size={14} /> Add Field
        </button>
      </div>

      {error && <p className="mt-3 text-xs font-semibold text-red-600 dark:text-red-400" data-testid="wizard-fields-error">{error}</p>}
      <button type="button" onClick={save} disabled={saving} data-testid="wizard-fields-save-btn" className={`${btn} mt-5`}>
        {saving && <Loader2 size={14} className="animate-spin" />} Save & Continue
      </button>
    </div>
  );
};
